/**
 * Match chronicle — what the staff keeps for the history books. Collected client-side from the
 * player's own views, so it only ever holds what this player was allowed to know (an observer —
 * a capitulated nation of a free-for-all — keeps sampling the scoreboard from what it watches,
 * but records only public events: no other staff's signals become "our" history):
 *  - the notable events of the whole match (the game store's log keeps only the last 300 lines,
 *    most of them routine signals traffic),
 *  - a time series of every faction's victory points (public scoreboard) and believed territory.
 *
 * Feeds the Morning Dispatch newspaper and the after-action report. `installChronicle()` is
 * called once at startup so no tick is missed, whichever screen is mounted.
 */
import { type GameEvent, type GameMap, NO_FACTION, type PlayerView } from '@friction/engine';
import { createStore } from 'zustand/vanilla';
import { gameStore } from './store';

export interface ChronicleSample {
  tick: number;
  /** Victory points per faction id. */
  vp: number[];
  /** Hexes believed held per faction id (own territory is exact, the enemy's as last seen). */
  hexes: number[];
}

export interface ChronicleState {
  /** Map of the match this chronicle belongs to (a new map starts a new chronicle). */
  map: GameMap | null;
  events: GameEvent[];
  samples: ChronicleSample[];
  /** Current sampling interval in ticks; doubles each time the series is compacted. */
  every: number;
}

/** Event kinds worth keeping for the record (routine signals traffic is left to the teletype). */
export const NOTABLE_KINDS: ReadonlySet<GameEvent['kind']> = new Set<GameEvent['kind']>([
  'warDeclared',
  'battleStarted',
  'battleEnded',
  'cityCaptured',
  'encircled',
  'intercept',
  'unitDestroyed',
  'unitProduced',
  'techResearched',
  'decoyExposed',
  'hqLost',
  'courierLost',
  'capitulated',
  'gameOver',
]);

/** Samples kept before the series is thinned to every other point. */
export const MAX_SAMPLES = 720;
/** Events kept (oldest dropped first); a long war rarely comes near it. */
const MAX_EVENTS = 6000;

/** One data point from a view: public VP and the believed map. */
export function sampleView(view: PlayerView): ChronicleSample {
  const n = view.factions.reduce((m, f) => Math.max(m, f.id + 1), 0);
  const vp = new Array<number>(n).fill(0);
  for (const f of view.factions) vp[f.id] = f.vp;
  const hexes = new Array<number>(n).fill(0);
  const owner = view.owner;
  for (let h = 0; h < owner.length; h++) {
    const o = owner[h];
    if (o !== NO_FACTION && o < n) hexes[o]++;
  }
  return { tick: view.tick, vp, hexes };
}

/**
 * Append a sample if at least `every` ticks passed since the last one (or `force`, used for the
 * final tick of the war). When the series exceeds `max`, every other point is dropped and the
 * interval doubles, so memory stays bounded while the whole war remains covered (the first and
 * the latest points are always kept).
 */
export function appendSample(
  samples: readonly ChronicleSample[],
  sample: ChronicleSample,
  every: number,
  max = MAX_SAMPLES,
  force = false,
): { samples: ChronicleSample[]; every: number } {
  const last = samples[samples.length - 1];
  if (last && sample.tick <= last.tick) {
    // The same hour again (orders applied between ticks, the final verdict): refresh the tail.
    if (sample.tick === last.tick) return { samples: [...samples.slice(0, -1), sample], every };
    return { samples: samples as ChronicleSample[], every };
  }
  if (last && !force && sample.tick < last.tick + every)
    return { samples: samples as ChronicleSample[], every };
  let next = [...samples, sample];
  let ev = every;
  if (next.length > max) {
    next = next.filter((_, i) => i % 2 === 0 || i === next.length - 1);
    ev *= 2;
  }
  return { samples: next, every: ev };
}

export const chronicleStore = createStore<ChronicleState>()(() => ({
  map: null,
  events: [],
  samples: [],
  every: 1,
}));

/** Notable events with `from <= tick < to`. */
export function eventsBetween(events: readonly GameEvent[], from: number, to: number): GameEvent[] {
  return events.filter((e) => e.tick >= from && e.tick < to);
}

/** Start recording; returns the unsubscribe function. */
export function installChronicle(): () => void {
  return gameStore.subscribe((st, prev) => {
    // Replay frames ("The Fog Lifts") are history already on record, often omniscient: never
    // record them, nor the live view coming back when the replay closes.
    if (!st.view || !st.map || st.replay || prev.replay) return;
    const c = chronicleStore.getState();
    const fresh = st.map !== c.map;
    if (fresh) chronicleStore.setState({ map: st.map, events: [], samples: [], every: 1 });
    const { events, samples, every } = chronicleStore.getState();
    const patch: Partial<ChronicleState> = {};
    if (fresh || st.freshEvents !== prev.freshEvents) {
      const notable = st.freshEvents.filter(
        (e) => NOTABLE_KINDS.has(e.kind) && (st.observing === null || e.audience === 'all'),
      );
      if (notable.length) patch.events = [...events, ...notable].slice(-MAX_EVENTS);
    }
    if (fresh || st.view.tick !== prev.view?.tick || st.view.winners !== prev.view?.winners) {
      const over = st.view.winners !== null;
      const next = appendSample(samples, sampleView(st.view), every, MAX_SAMPLES, over);
      if (next.samples !== samples) Object.assign(patch, next);
    }
    if (Object.keys(patch).length) chronicleStore.setState(patch);
  });
}
